import * as React from "react"
import type { RendererProps } from "../core/element-registry"
import { ElementRenderer } from "./element-renderer"
import {
  isSourceDerivedElement,
  readSourceFrame,
  sourceFillBoxStyle,
  sourceInlineStyle,
} from "./source-derived-rendering"

type UnknownRecord = Record<string, unknown>

export function SourceApplicationRenderer({ element }: RendererProps) {
  const children = element.children ?? []

  return (
    <div
      data-testid={`source-application-${element.id}`}
      data-cogo-source-application="true"
      data-cogo-source-derived="true"
      style={sourceInlineStyle(element)}
    >
      {children.map((child, index) => (
        <ElementRenderer key={child.id ?? `source-app-child-${index}`} element={child} />
      ))}
    </div>
  )
}

export function SourceScreenRenderer({ element }: RendererProps) {
  const frame = readSourceFrame(element)
  const children = element.children ?? []
  const style: React.CSSProperties = {
    position: "relative",
    overflow: "hidden",
    flex: "0 0 auto",
    ...(frame
      ? {
          width: frame.width,
          height: frame.height,
          minWidth: frame.width,
          minHeight: frame.height,
        }
      : {}),
    ...sourceInlineStyle(element),
  }

  return (
    <section
      data-testid={`source-screen-${element.id}`}
      data-cogo-source-screen="true"
      data-cogo-source-derived="true"
      data-surface-ref={String((element as unknown as UnknownRecord).surfaceRef ?? "") || undefined}
      style={style}
    >
      {children.map((child, index) => (
        <ElementRenderer key={child.id ?? `source-screen-child-${index}`} element={child} />
      ))}
    </section>
  )
}

export function ShapeRenderer({ element }: RendererProps) {
  const sourceDerived = isSourceDerivedElement(element)
  return (
    <div
      data-testid={`shape-${element.id}`}
      data-cogo-source-shape="true"
      data-cogo-source-derived={sourceDerived ? "true" : undefined}
      style={{
        ...sourceFillBoxStyle(element),
        ...(sourceDerived ? { pointerEvents: "none" } : {}),
      }}
    />
  )
}
